/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useUserData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading user data and options.
────────────────────────────────────────────────────────────────*/

import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useMenuStore } from '../../../store/menu';
import {
  fetchUserById,
  fetchUserOptions,
} from '../api/usersApi';
import { UserFormData } from './useUserForm';

export function useUserData(
  id: string | undefined,
  duplicateId: string | null,
  mode: 'create' | 'edit',
  setFormData: React.Dispatch<React.SetStateAction<UserFormData>>,
  setOriginalData: React.Dispatch<React.SetStateAction<UserFormData>>,
  setAvatar: (url: string | null) => void,
  setOriginalAvatar: (url: string | null) => void,
) {
  const { user: currentUser } = useAuthStore();
  const { t } = useI18n();
  const { showError, showInfo } = useNotifications();
  const { getFinalItems } = useMenuStore();
  const tenantSlug = currentUser?.tenant?.slug || t('users.defaultTenant');

  const [profiles, setProfiles] = useState<
    Array<{ id: string; name: string; description?: string }>
  >([]);
  const [logProfiles, setLogProfiles] = useState<
    Array<{ id: string; name: string; description?: string }>
  >([]);
  const [tenants, setTenants] = useState<
    Array<{ id: string; name: string; slug: string }>
  >([]);
  const [finalMenuItems, setFinalMenuItems] = useState<
    Array<{ id: number; name: string; path: string }>
  >([]);
  const [isLoadingMenuItems, setIsLoadingMenuItems] = useState(false);

  const userLoadedRef = useRef<string | null>(null);
  const formResetRef = useRef<boolean>(false);
  const lastIdRef = useRef<string | undefined>(id);
  const optionsLoadedRef = useRef(false);
  const loadingOptionsRef = useRef(false);
  const userNotFoundRef = useRef<string | null>(null);

  const getFullAvatarUrl = (avatarPath: string | null): string | null => {
    if (!avatarPath) return null;
    return avatarPath.startsWith('http') ? avatarPath : `${avatarPath}`;
  };

  // Load user data
  useEffect(() => {
    const loadUser = async () => {
      const loadId = mode === 'edit' ? id : duplicateId;
      const currentId = id || 'new';
      const previousId = lastIdRef.current;
      const idChangedToNew = previousId && previousId !== 'new' && currentId === 'new';

      if (!loadId || loadId === 'new') {
        // Reset the flag if we were previously loading a user OR if ID changed to 'new'
        if (idChangedToNew || userLoadedRef.current !== null) {
          formResetRef.current = false;
        }
        userLoadedRef.current = null;

        // Reset form when navigating to new (either initial load or after save)
        if (!formResetRef.current || idChangedToNew) {
          formResetRef.current = true;
          // Clear the not found flag when navigating to new
          userNotFoundRef.current = null;
          // Reset form when navigating to new
          setFormData({
            username: '',
            email: '',
            password: '',
            fullName: '',
            department: '',
            locale: 'en',
            timezone: 'UTC',
            startupDialog: '/dashboard',
            totpEnabled: false,
            isActive: true,
            debugMode: false,
            isPrimaryAdmin: false,
            extensionId: null,
            profileId: '',
            logProfileId: '',
            tenantIds: currentUser?.tenant?.id ? [currentUser.tenant.id.toString()] : [],
            chatbotEnabled: false,
            chatbotAgentId: null,
            appsConfigs: {
              coreEnabled: true,
              switchboardEnabled: true,
              switchboardRole: '',
              switchboardLogProfileId: '',
              switchboardUrl: '',
              portalEnabled: true,
              portalUrl: '',
            },
          });
          setOriginalData({
            username: '',
            email: '',
            password: '',
            fullName: '',
            department: '',
            locale: 'en',
            timezone: 'UTC',
            startupDialog: '/dashboard',
            totpEnabled: false,
            isActive: true,
            debugMode: false,
            isPrimaryAdmin: false,
            extensionId: null,
            profileId: '',
            logProfileId: '',
            tenantIds: currentUser?.tenant?.id ? [currentUser.tenant.id.toString()] : [],
            chatbotEnabled: false,
            chatbotAgentId: null,
            appsConfigs: {
              coreEnabled: true,
              switchboardEnabled: true,
              switchboardRole: '',
              switchboardLogProfileId: '',
              switchboardUrl: '',
              portalEnabled: true,
              portalUrl: '',
            },
          });
          setAvatar(null);
          setOriginalAvatar(null);
        }

        lastIdRef.current = currentId;
        return;
      }

      // Reset the form reset flag when loading an actual user
      formResetRef.current = false;

      // If we already know this user doesn't exist, don't try again
      if (userNotFoundRef.current === loadId) {
        lastIdRef.current = currentId;
        return;
      }

      if (userLoadedRef.current === loadId) {
        lastIdRef.current = currentId;
        return;
      }
      userLoadedRef.current = loadId;
      lastIdRef.current = currentId;

      try {
        const userData = await fetchUserById(loadId, tenantSlug, currentUser?.id);

        // Clear the not found flag if we successfully loaded the user
        userNotFoundRef.current = null;

        const loadedData: UserFormData = {
          username: mode === 'edit' ? userData.username : `${userData.username}_copy`,
          email: mode === 'edit' ? userData.email : `copy_${userData.email}`,
          password: '',
          fullName: userData.fullName || '',
          department: userData.department || '',
          locale: userData.locale,
          timezone: userData.timezone || 'UTC',
          startupDialog: userData.startupDialog || '/dashboard',
          totpEnabled: userData.totpEnabled || false,
          isActive: userData.isActive,
          debugMode: userData.debugMode ?? false,
          isPrimaryAdmin: (userData as any).isPrimaryAdmin ?? false,
          extensionId: (userData as any).extensionId ?? null,
          // Role and LogProfile are now directly on the user
          profileId: (userData as any).role?.id?.toString() || '',
          logProfileId: (userData as any).logProfile?.id?.toString() || '',
          // Use carefully mapped tenantIds from API, preserving empty state if user intentionally cleared it
          tenantIds: userData.tenants ? userData.tenants.map(t => t.id.toString()) : [],
          chatbotEnabled: (userData as any).chatbotEnabled ?? false,
          chatbotAgentId: (userData as any).chatbotAgentId ?? null,
          appsConfigs: {
            coreEnabled: true,
            switchboardEnabled: (userData as any).appPrefs?.switchboardEnabled ?? (userData as any).themePrefs?.apps?.switchboardEnabled ?? true,
            switchboardRole: (userData as any).appPrefs?.switchboardRole || (userData as any).themePrefs?.apps?.switchboardRole || '',
            switchboardLogProfileId: (userData as any).appPrefs?.switchboardLogProfileId?.toString() || '',
            switchboardUrl: (userData as any).appPrefs?.switchboardUrl || (userData as any).themePrefs?.apps?.switchboardUrl || (userData as any).themePrefs?.apps_urls?.switchboard || '',
            portalEnabled: (userData as any).appPrefs?.portalEnabled ?? (userData as any).themePrefs?.apps?.portalEnabled ?? true,
            portalUrl: (userData as any).appPrefs?.portalUrl || (userData as any).themePrefs?.apps?.portalUrl || (userData as any).themePrefs?.apps_urls?.portal || '',
          }
        };

        if (duplicateId) {
          showInfo(t('users.messages.duplicateHint'));
        }

        setFormData(loadedData);
        setOriginalData(loadedData);

        if ((userData as any).avatar) {
          const avatarUrl = getFullAvatarUrl((userData as any).avatar);
          setAvatar(avatarUrl);
          setOriginalAvatar(avatarUrl);
        } else {
          setOriginalAvatar(null);
        }
      } catch (error) {
        // Check if it's a 404 error
        const isNotFound = error instanceof Error &&
          (error.message.includes('404') ||
            error.message.includes('status: 404') ||
            error.message.toLowerCase().includes('not found'));

        if (isNotFound) {
          // Mark this user as not found to prevent retrying
          userNotFoundRef.current = loadId;
          showError(t('users.errors.errorLoadingUser'));
        } else {
          showError(t('users.errors.errorLoadingUser'));
        }
        userLoadedRef.current = null;
      }
    };

    loadUser();
  }, [
    id,
    duplicateId,
    mode,
    tenantSlug,
    currentUser?.id,
    setFormData,
    setOriginalData,
    setAvatar,
    setOriginalAvatar,
    showError,
    showInfo,
    t,
  ]);

  // Load options data - use ref to prevent multiple simultaneous calls
  useEffect(() => {
    const loadOptions = async () => {
      // Prevent multiple simultaneous calls
      if (loadingOptionsRef.current || optionsLoadedRef.current) {
        return;
      }

      if (!tenantSlug || !currentUser?.id) {
        return;
      }

      try {
        loadingOptionsRef.current = true;
        const options = await fetchUserOptions(tenantSlug, currentUser.id);
        setProfiles(options.profiles);
        setLogProfiles(options.logProfiles);
        setTenants(options.tenants);
        optionsLoadedRef.current = true;
      } catch (error) {
        // Only show error if it's not a connection refused error (server might be down)
        // Use the latest showError and t from closure, but don't add them to dependencies
        if (error instanceof Error && !error.message.includes('ERR_CONNECTION_REFUSED')) {
          showError(t('users.errors.failedToLoadOptions'));
        }
      } finally {
        loadingOptionsRef.current = false;
      }
    };

    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug, currentUser?.id]);

  // Load menu items
  useEffect(() => {
    const loadFinalMenuItems = async () => {
      setIsLoadingMenuItems(true);
      try {
        const items = getFinalItems();
        const transformedItems = items.map((item) => {
          let translated = t(`menu.sections.${item.title}`);
          if (translated === `menu.sections.${item.title}`) translated = item.title;
          return {
            id: item.id,
            name: translated,
            path: item.url || '',
          };
        });

        // Explicitly inject Main Dashboard as the first option
        const dashboardLabel = t('menu.sections.dashboard');
        transformedItems.unshift({
            id: 0,
            name: dashboardLabel !== 'menu.sections.dashboard' ? dashboardLabel : 'Dashboard',
            path: '/dashboard'
        });

        setFinalMenuItems(transformedItems);

        // Sanitize any invalid startupDialog values fetched from DB (e.g. legacy 'dashboard')
        setFormData(prev => {
          if (prev.startupDialog && !transformedItems.some(m => m.path === prev.startupDialog)) {
            return { ...prev, startupDialog: '' };
          }
          return prev;
        });
      } catch (error) {
        showError(t('users.errors.failedToLoadMenuItems'));
      } finally {
        setIsLoadingMenuItems(false);
      }
    };

    loadFinalMenuItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    profiles,
    logProfiles,
    tenants,
    finalMenuItems,
    isLoadingMenuItems,
  };
}

